import {
  Box,
  Text,
  Group,
  Avatar,
  Stack,
  ActionIcon,
  ScrollArea,
  useMantineTheme,
  Indicator,
} from '@mantine/core';
import { forwardRef } from 'react';
import {
  IconDots,
  IconMessageCircle,
  IconHeart,
  IconShare,
  IconBookmark,
} from '@tabler/icons-react';
import { Paper, Badge } from '../../ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ActivityItem {
  id: string;
  type: 'comment' | 'like' | 'share' | 'create' | 'update' | 'delete' | 'mention' | 'follow' | 'custom';
  user: {
    name: string;
    avatar?: string;
    online?: boolean;
  };
  action: string;
  target?: string;
  content?: string;
  timestamp: Date | string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
  unread?: boolean;
}

export interface ActivityFeedProps {
  items: ActivityItem[];
  title?: string;
  maxHeight?: number | string;
  showTimestamp?: boolean;
  showAvatar?: boolean;
  onItemClick?: (item: ActivityItem) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'default' | 'compact' | 'detailed';
}

const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return then.toLocaleDateString();
};

const getActivityIcon = (type: ActivityItem['type']) => {
  switch (type) {
    case 'comment':
      return <IconMessageCircle size={14} />;
    case 'like':
      return <IconHeart size={14} />;
    case 'share':
      return <IconShare size={14} />;
    case 'follow':
      return <IconBookmark size={14} />;
    default:
      return null;
  }
};

const ActivityFeed = forwardRef<HTMLDivElement, ActivityFeedProps>(
  (
    {
      items,
      title = 'Recent Activity',
      maxHeight = 400,
      showTimestamp = true,
      showAvatar = true,
      onItemClick,
      onLoadMore,
      hasMore,
      loading = false,
      emptyMessage = 'No recent activity',
      gradientVariant = 'primary',
      variant = 'default',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const renderActivityItem = (item: ActivityItem) => {
      const isCompact = variant === 'compact';
      const isDetailed = variant === 'detailed';

      return (
        <Box
          key={item.id}
          p={isCompact ? 'xs' : 'sm'}
          style={{
            cursor: onItemClick ? 'pointer' : 'default',
            backgroundColor: item.unread ? `${gradient?.from}08` : undefined,
            borderLeft: item.unread ? `3px solid ${gradient?.from}` : '3px solid transparent',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: theme.colors.gray[0],
            },
          }}
          onClick={() => onItemClick?.(item)}
        >
          <Group wrap="nowrap" align="flex-start">
            {showAvatar && (
              <Indicator
                disabled={!item.user.online}
                color="teal"
                size={10}
                offset={4}
                position="bottom-end"
              >
                <Avatar
                  src={item.user.avatar}
                  alt={item.user.name}
                  size={isCompact ? 'sm' : 'md'}
                  radius="xl"
                >
                  {item.user.name.charAt(0).toUpperCase()}
                </Avatar>
              </Indicator>
            )}

            <Box style={{ flex: 1, minWidth: 0 }}>
              <Group gap={4} wrap="nowrap">
                <Text size={isCompact ? 'xs' : 'sm'} fw={600} truncate style={accessibilityStyles}>
                  {item.user.name}
                </Text>
                <Text size={isCompact ? 'xs' : 'sm'} c="dimmed" truncate style={accessibilityStyles}>
                  {item.action}
                </Text>
                {item.target && (
                  <Text
                    size={isCompact ? 'xs' : 'sm'}
                    fw={500}
                    truncate
                    style={{ ...accessibilityStyles, color: gradient?.from }}
                  >
                    {item.target}
                  </Text>
                )}
              </Group>

              {item.content && isDetailed && (
                <Text size="sm" c="dimmed" mt={4} lineClamp={2} style={accessibilityStyles}>
                  {item.content}
                </Text>
              )}

              {showTimestamp && (
                <Group gap="xs" mt={4}>
                  {item.icon || getActivityIcon(item.type)}
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatRelativeTime(item.timestamp)}
                  </Text>
                </Group>
              )}
            </Box>

            {item.unread && (
              <Box
                w={8}
                h={8}
                style={{
                  borderRadius: '50%',
                  backgroundColor: gradient?.from,
                  flexShrink: 0,
                }}
              />
            )}
          </Group>
        </Box>
      );
    };

    return (
      <Paper ref={ref} variant="outlined" p={0}>
        {/* Header */}
        <Group justify="space-between" p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
          <Group gap="xs">
            <Text fw={600} style={accessibilityStyles}>{title}</Text>
            {items.some((i) => i.unread) && (
              <Badge size="sm" variant="filled" color={gradient?.from}>
                {items.filter((i) => i.unread).length} new
              </Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" size="sm">
            <IconDots size={16} />
          </ActionIcon>
        </Group>

        {/* Activity list */}
        <ScrollArea h={maxHeight} type="auto">
          {items.length === 0 ? (
            <Box p="xl" ta="center">
              <Text c="dimmed" size="sm" style={accessibilityStyles}>
                {emptyMessage}
              </Text>
            </Box>
          ) : (
            <Stack gap={0}>
              {items.map(renderActivityItem)}

              {hasMore && (
                <Box
                  p="sm"
                  ta="center"
                  style={{ cursor: 'pointer' }}
                  onClick={onLoadMore}
                >
                  <Text size="sm" c={gradient?.from} fw={500} style={accessibilityStyles}>
                    {loading ? 'Loading...' : 'Load more'}
                  </Text>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea>
      </Paper>
    );
  }
);

ActivityFeed.displayName = 'ActivityFeed';

export default ActivityFeed;
